import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import s from "../modules/MySubs.module.css";
import { useT, useI18nReady } from "./TextProvider";

// ===== helpers: auth
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") ||
    s.getItem("auth:token") ||
    s.getItem("auth:key") ||
    s.getItem("auth:jwt") ||
    s.getItem("auth:auth_token") ||
    null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}

function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME;
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

// ===== API endpoints
const FANLAR_ENDPOINT =
  process.env.REACT_APP_FANLAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/fanlar/";

export default function MySubs() {
  const t = useT();
  const ready = useI18nReady();
  const navigate = useNavigate();

  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  // Загрузка текущего пользователя
  useEffect(() => {
    const user = getStoredUser();
    setCurrentUser(user);
  }, []);

  // Загрузка списка предметов
  useEffect(() => {
    async function fetchSubjects() {
      try {
        setLoading(true);
        setError("");
        const token = getAnyTokenFromStorage();

        const res = await fetch(FANLAR_ENDPOINT, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? makeAuthHeader(token) : {}),
          },
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error("Fanlarga kirish ruxsati berilmadi");
        }

        const data = await res.json();
        let allSubjects = Array.isArray(data) ? data : [];

        // НЕ фильтруем предметы - показываем ВСЕ
        setSubjects(allSubjects);
      } catch (err) {
        setError(err?.message || "Fanlari yuklashda xatolik yuz berdi");
        setSubjects([]);
      } finally {
        setLoading(false);
      }
    }

    fetchSubjects();
  }, []);

  function handleAddNew() {
    navigate("/YangiFan");
  }

  function handleEditSubject(id) {
    navigate(`/EditSubs/${id}`);
  }

  return (
    <div className={s.wrapper}>
      <div className={s.card}>
        <div className={s.header}>
          <h1 className={s.title}>Mening Fanlarim</h1>
          <button
            className={s.addBtn}
            onClick={handleAddNew}
            title="Yangi fan yaratish"
            aria-label="Yangi fan yaratish"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "8px", display: "inline" }}>
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Yangi Fan
          </button>
        </div>

        {loading && (
          <div className={s.loading}>
            Fanlari yuklayapmiz...
          </div>
        )}

        {error && !loading && (
          <div className={s.error}>
            {error}
          </div>
        )}

        {!loading && subjects.length === 0 && (
          <div className={s.empty}>
            <p>Hozircha fanlar mavjud emas</p>
            <button className={s.emptyBtn} onClick={handleAddNew} type="button">
              Birinchi Fanni Yaratish
            </button>
          </div>
        )}

        {!loading && subjects.length > 0 && (
          <div className={s.list}>
            {subjects.map((subject) => (
              <div 
                key={subject.id} 
                className={s.item}
                onClick={() => handleEditSubject(subject.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleEditSubject(subject.id);
                  }
                }}
              >
                {subject.img && (
                  <div className={s.itemImg}>
                    <img 
                      src={subject.img} 
                      alt={subject.name}
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/150?text=No+Image";
                      }}
                    />
                  </div>
                )}
                <div className={s.itemContent}>
                  <h3 className={s.itemTitle}>{subject.name}</h3>
                  <p className={s.itemDesc}>
                    {subject.description}
                  </p>
                  <div className={s.itemMeta}>
                    <span className={s.itemId}>ID: {subject.id}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
